import React, { useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

import { CustomEditor } from '../../core/CustomEditor';
import {
  MeetingRequestAction,
  useGetMeetingRequestMailTemplate,
} from '../../hooks/reactQuerys/MeetingRequestQueries';

interface IProps {
  idMeetingRequest: number;
  action: MeetingRequestAction;
  subject: string;
  onSubjectChange: (value: string) => void;
  body: string;
  onBodyChange: (value: string) => void;
}

/**
 * Lädt die Mandanten-Mailvorlage für die gewählte Aktion, füllt Betreff/Body
 * einmalig vor und zeigt sie zum Bearbeiten an (CustomEditor inkl.
 * Variablen-Button). Die {{Platzhalter}} bleiben sichtbar und werden erst beim
 * Versand im Backend ersetzt. Beim Öffnen je Dialog über `key` neu mounten,
 * damit die Vorfüllung erneut greift.
 */
export const MeetingRequestMailEditor: React.FC<IProps> = ({
  idMeetingRequest,
  action,
  subject,
  onSubjectChange,
  body,
  onBodyChange,
}) => {
  const { template, isLoadingTemplate } = useGetMeetingRequestMailTemplate(
    idMeetingRequest,
    action
  );
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (template && !prefilledRef.current) {
      prefilledRef.current = true;
      onSubjectChange(template.Subject ?? '');
      onBodyChange(template.Body ?? '');
    }
  }, [template, onSubjectChange, onBodyChange]);

  if (isLoadingTemplate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TextField
          label='Betreff'
          value={subject}
          onChange={event => onSubjectChange(event.target.value)}
          fullWidth
          size='small'
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant='caption' color='text.secondary'>
          Die Variablen in doppelten geschweiften Klammern (z.B.{' '}
          {'{{Meeting_at}}'}) werden erst beim Versand mit echten Werten
          ersetzt.
        </Typography>
      </Grid>
      <CustomEditor
        txt={body}
        setTxt={onBodyChange}
        idTextModuleCategory={1}
        dictonaryIdsArray={template?.DictionaryIds ?? [1, 2, 3, 14]}
      />
    </Grid>
  );
};

export default MeetingRequestMailEditor;
